import React, { useMemo, useState, useRef,useEffect, useCallback } from 'react';
import moment from 'moment';
import { Box, Button, Checkbox, Dialog, FormControlLabel, IconButton, InputBase, makeStyles } from '@material-ui/core';
import { KeyboardDateTimePicker } from '@material-ui/pickers';
import _ from 'lodash';
import { useSelector } from 'react-redux';
import ehandoverIds from '../../../../ids/Ehandover';
import { i18n } from '../../../../language';
import { CloseIcon, DateIcon } from '../../../../components/Icon';
import { AutoCompleteSelect } from '../../../../components';
import { classNames, getArrayRandomKey } from '../../../../utils';
import { checkDateForRules } from '../../../../utils/checkDateForRules';
import { getInitCustomRecurrence } from '../../../../utils/eHandoverUtil';
import { LANGUAGE_EN_US } from '../../../../comm/config';
import { generateScheduleDates } from '../../../../utils/scheduleDateUtil';


const minMinutes = 20;
const useStyles = makeStyles((theme) => ({
  pop: {
    '& .MuiDialog-paperWidthSm': {
      maxWidth: 'none',
    },
    '& .MuiPaper-root': {
      width: '43.75rem',
      backgroundColor: theme.palette.font.white,
      borderRadius: '0.9375rem',
      boxShadow: '0 -0.125rem 1.3125rem rgba(30, 56, 140, 0.18)',
      margin: '0',
    },
  },
  poproot: {
    padding: '2.375rem 1.563rem 1.875rem',
    width: '43.75rem',
  },
  head: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '2.125rem',
  },
  title: {
    fontFamily: 'NotoSans-Bold',
    fontSize: '1.5rem',
    lineHeight: '1.875rem',
    letterSpacing: '0',
    color: theme.palette.font.main,
    fontWeight: 'bold',
  },
  icon: {
    fontSize: '1.375rem',
    color: theme.palette.primary.main,
  },
  context: {
    fontFamily: 'NotoSans-Medium',
    fontSize: '0.875rem',
    color: theme.palette.font.sub,
    fontWeight: '500',
    marginBottom: '0.625rem',
  },
  datepicker: {
    width: '100%',
    '& .MuiInputBase-root': {
      padding: '0 1rem 0 1.25rem',
      borderRadius: '0.625rem',
      border: '0.0625rem solid #e4e7eb',
      fontFamily: 'NotoSans-Medium',
      fontSize: '1rem',
      color: theme.palette.font.main,
      fontWeight: 'normal',
    },
    '& .MuiPaper-root': {
      borderRadius: '0.9375rem',
    },
  },
  Picon: {
    width: '1.125rem',
    height: '1.25rem',
    color: theme.palette.primary.main,
  },
  foot: {
    marginTop: '3.25rem',
    display: 'flex',
    justifyContent: 'center',
  },
  cancel: {
    textTransform: 'capitalize',
    fontFamily: 'NotoSans-Medium',
    fontSize: '1rem',
    lineHeight: '1.25rem',
    backgroundColor: '#eff6ff',
    color: theme.palette.primary.main,
    fontWeight: '500',
    borderRadius: '0.625rem',
    border: '0.12rem solid #538fff',
    width: '12.5rem',
    height: '3rem',
    marginRight: '1.25rem',
  },
  confirm: {
    textTransform: 'capitalize',
    width: '12.5rem',
    height: '3rem',
    borderRadius: '0.5rem',
    backgroundColor: '#4c93ff',
    fontFamily: 'NotoSans-Medium',
    fontSize: '1rem',
    lineHeight: '1.25rem',
    color: theme.palette.font.white,
    fontWeight: '500',
    '&:hover': {
      backgroundColor: '#4c93ff',
    },
    '& .MuiButtonBase-root.Mui-disabled': {
      color: theme.palette.font.white,
    },
  },
  checkBox: {
    marginLeft: '0rem',
    marginRight: '0rem',
    '& .MuiButtonBase-root': {
      padding: '0',
      paddingRight: '0.4rem',
    },
    '& .MuiCheckbox-root': {
      color: '#c0c6cb',
    },
    '& .MuiTypography-root': {
      fontFamily: 'NotoSans-Medium',
      fontSize: '1rem',
      fontWeight: '500',
      color: '#2e4469',
    },
    '& .Mui-checked': {
      color: '#28d2ab',
    },
    '& .Mui-checked.Mui-disabled': {
      color: '#c0c6cb',
    },
  },
  repeatLabel: {
    fontSize: '1.142rem',
    fontWeight: 500,
    fontFamily: 'NotoSans-Medium',
  },
  inputBase: ({ error }) => ({
    border: error ? 'solid 1px #f44336' : 'solid 1px #e4e7eb',
    borderRadius: '0.625rem',
    height: '3.5rem',
    width: '18rem',
    padding: '0 1.25rem',
    fontFamily: 'NotoSans-Medium',
    fontSize: '1rem',
    color: '#2e4469',
    flex: 1,
  }),
  weekBox: {
    padding: '0.785rem 0',
    borderRadius: '0.571rem',
    border: '1px solid #E4E7EB',
    fontSize: '1.142rem',
    fontWeight: 500,
    fontFamily: 'NotoSans-Medium',
    cursor: 'pointer',
    flex: 1,
    textAlign: 'center',
  },
  active: {
    color: '#538FFF',
    border: '1px solid #538FFF',
    backgroundColor: 'linear-gradient(0deg, #F5FAFF 0%, #F5FAFF 100%), #28D2AB',
  },
  tips: {
    fontFamily: 'NotoSans-Medium',
    color: '#f44336',
  },
}));

const SCHEDULE_DATE_FORMAT = 'YYYY-MM-DD HH:mm:ss';
const WEEK_FULL_NAME_MAP = {
  1: 'Monday',
  2: 'Tuesday',
  3: 'Wednesday',
  4: 'Thursday',
  5: 'Friday',
  6: 'Saturday',
  7: 'Sunday',
};

const formatScheduleDate = (date) => moment(date).format(SCHEDULE_DATE_FORMAT);

const getWeekScheduleDateList = ({ selectedDate, intervalValue, daysOfWeek, endDate }) => {
  const generateEndDate = endDate
    ? moment(endDate).format('yyyy-MM-DD 23:59:59')
    : moment(selectedDate)
        .add(intervalValue + 1, 'weeks')
        .format('yyyy-MM-DD 23:59:59');

  return generateScheduleDates(
    formatScheduleDate(selectedDate),
    intervalValue,
    'WEEK',
    new Set(daysOfWeek),
    generateEndDate,
    null,
    null
  );
};

const getWeekFirstSendDate = (scheduleDateList, weekVal) => {
  return _.find(scheduleDateList, (scheduleDate) => {
    return moment(scheduleDate, SCHEDULE_DATE_FORMAT).isoWeekday() === Number(weekVal);
  });
};

const getWeekOverLimitInfo = ({ scheduleDateList, selectedWeekOptions, startMoment }) => {
  for (const item of selectedWeekOptions) {
    const firstSendDate = getWeekFirstSendDate(scheduleDateList, item.val);
    if (!firstSendDate) {
      continue;
    }

    if (moment(firstSendDate, SCHEDULE_DATE_FORMAT).diff(startMoment, 'days', true) > 7) {
      return { weekDay: item.text, weekVal: Number(item.val), firstSendDate };
    }
  }

  return null;
};

const roundUpToNearest10Minutes = (momentInstance) => {
  const intervals = 10;
  const remainder = intervals - (momentInstance.minute() % intervals);
  
  // If already exactly on a 10-minute mark, remainder % intervals adds 0 minutes
  return momentInstance
    .add(remainder % intervals, 'minutes')
    .startOf('minute');
}

export default function ScheduleSendDialog({
  open,
  setShowDialog,
  setDateDrop,
  handleShare,
  scheduleDate = null,
  initCustomRecurrence = null,
  customRecurrenceDisabled = false,
  disabledCustomCheckbox = false,
}) {
  const inputRef = useRef(null);
  const classes = useStyles();
  const minDate = moment().add(minMinutes, 'm').format('yyyy-MM-DD HH:mm:00');
  const [selectedDate, setSelectedDate] = useState(
    scheduleDate ? scheduleDate : roundUpToNearest10Minutes(moment(minDate))
  );
  // 选中的schedule date至少是现在的一个小时之后
  const disabledEarierThanMinDate = moment(selectedDate).diff(minDate, 'minutes') < 0;
  const [customConfig, setCustomConfig] = useState(
    initCustomRecurrence ?? {
      ...getInitCustomRecurrence(),
    }
  );
  const language = useSelector((state) => state.settings.language);
  const endDateMaxDate = useMemo(() => moment(selectedDate).add(2, 'y').format('yyyy-MM-DD 23:59:59'), [selectedDate]);
  const endDateOverMaxDate =
    customConfig?.customRecurrence &&
    customConfig?.endDate &&
    moment(customConfig?.endDate).isValid() &&
    moment(customConfig?.endDate).isAfter(moment(endDateMaxDate));
  const intervalValueEmpty =
    customConfig?.customRecurrence && (customConfig?.intervalValue === '' || customConfig?.intervalValue === null);
  const weekDaysOfWeekEmpty =
    customConfig?.customRecurrence && customConfig?.intervalType === 'WEEK' && _.isEmpty(customConfig?.daysOfWeek);
  const monthRepeatTypeEmpty =
    customConfig?.customRecurrence && customConfig?.intervalType === 'MONTH' && !customConfig?.monthRepeatType;
  const shareDisabled = useMemo(
    () => disabledEarierThanMinDate || endDateOverMaxDate || intervalValueEmpty || weekDaysOfWeekEmpty || monthRepeatTypeEmpty || (!(selectedDate?.isValid() ?? false)) || 
        (customConfig?.customRecurrence && !(moment(customConfig?.endDate).isValid()))
  , [disabledEarierThanMinDate, endDateOverMaxDate ,intervalValueEmpty ,weekDaysOfWeekEmpty ,monthRepeatTypeEmpty ,selectedDate, customConfig?.endDate,customConfig?.customRecurrence]);
  

  const repeatUnitOptions = [
    {
      text: i18n.t('EHANDOVER_DAY'),
      value: 'DAY',
    },
    {
      text: i18n.t('EHANDOVER_WEEK'),
      value: 'WEEK',
    },
    {
      text: i18n.t('EHANDOVER_MONTH'),
      value: 'MONTH',
    },
  ];
  const weekOptions = [
    { text: i18n.t('EHANDOVER_WEEK_MON'), val: '1' },
    { text: i18n.t('EHANDOVER_WEEK_TUE'), val: '2' },
    { text: i18n.t('EHANDOVER_WEEK_WEB'), val: '3' },
    { text: i18n.t('EHANDOVER_WEEK_THUR'), val: '4' },
    { text: i18n.t('EHANDOVER_WEEK_FRI'), val: '5' },
    { text: i18n.t('EHANDOVER_WEEK_SAT'), val: '6' },
    { text: i18n.t('EHANDOVER_WEEK_SUN'), val: '7' },
  ];
  const maxRepeatNum = {
    DAY: 731,
    WEEK: 104,
    MONTH: 24,
  };
  const monthOptions = useMemo(() => {
    const rules = _.values(checkDateForRules(selectedDate));
    let options = [];
    if (Array.isArray(rules) && rules.length > 0 && customConfig?.intervalType === 'MONTH') {
      options = _.map(rules, (item) => ({
        value: item?.recurrenceType,
        text: item?.optionText,
        monthDayOfWeek: item?.dayOfWeek ?? null,
      }));
    }
    return options;
  }, [selectedDate, customConfig]);

  const handleDialogClose = () => {
    setShowDialog(false);
    setDateDrop(false);
    setSelectedDate(null);
  };
  const handleDialogSubmit = () => {
    if (shareDisabled) {
      return;
    }

    const customRecurrence = {
      ...customConfig,
      endDate: customConfig?.endDate ? moment(customConfig?.endDate).format('yyyy-MM-DD 23:59:59') : null,
      daysOfWeek: customConfig?.intervalType === 'WEEK' ? customConfig?.daysOfWeek : [],
      monthDayOfWeek: customConfig?.intervalType === 'MONTH' ? customConfig?.monthDayOfWeek : null,
      monthRepeatType: customConfig?.intervalType === 'MONTH' ? customConfig?.monthRepeatType : '',
    };
    handleShare({
      selectedDate,
      customRecurrence: customConfig?.customRecurrence ? customRecurrence : getInitCustomRecurrence(),
    });
    handleDialogClose();
  };
  const handleChangeRepeatNum = (val) => {
    const intVal = String(val ?? '').replace(/\D/g, '');

    if (intVal === '') {
      handleChangeCustomConfig('intervalValue', '');
      return;
    }

    const maxRepeatVal = maxRepeatNum?.[customConfig?.intervalType] ?? 99;
    let nextVal = Number(intVal);
    if (nextVal > maxRepeatVal) nextVal = maxRepeatVal;
    if (nextVal < 1) nextVal = 1;

    handleChangeCustomConfig('intervalValue', nextVal);
  };
  const handleSelectDayOfWeek = (val) => {
    let days = [];
    if (customConfig.daysOfWeek?.includes(val)) {
      days = _.filter(customConfig.daysOfWeek, (item) => item !== val);
    } else {
      days = [...customConfig.daysOfWeek, val];
    }
    handleChangeCustomConfig('daysOfWeek', days);
  };
  const handleChangeCustomConfig = (key, val) => {
    if(key === 'intervalValue'){
      setCustomConfig((prev) => ({
        ...prev,
        intervalValue: val,
      }));
      return
    }
    setCustomConfig((prev) => ({
      ...prev,
      intervalValue: prev?.intervalType && key === 'intervalType' ? '' : prev?.intervalValue,
      daysOfWeek: key === 'intervalType' ? [] : prev?.daysOfWeek,
      monthRepeatType: key === 'intervalType' ? '' : prev?.monthRepeatType,
      monthDayOfWeek: key === 'intervalType' ? null : prev?.monthDayOfWeek,
      endDate: key === 'intervalType' ? null : prev?.endDate,
      [key]: val,
    }));
  };
  const handleChangeMonthRepeatType = (item) => {
    handleChangeCustomConfig('monthRepeatType', item?.value);
    handleChangeCustomConfig('monthDayOfWeek', item?.monthDayOfWeek);
  };
  const handleChangeEndDate = (val) => {
    let newVal = val;
    // 如果选的是MONTH,自动把日期补全为当月的最后一天
    if (customConfig?.intervalType === 'MONTH' && val && moment(val).isValid()) {
      newVal = moment(val).endOf('month');
    }

    handleChangeCustomConfig('endDate', newVal);
  };
  const handleChangePostDate = (val) => {
    setSelectedDate(val !== null ? roundUpToNearest10Minutes(val): roundUpToNearest10Minutes(moment(minDate)));
    if (!customRecurrenceDisabled) {
      handleChangeMonthRepeatType({
        value: '',
        monthDayOfWeek: null,
      });
    }
  };
  const weekRepeatTypeTips = useMemo(() => {
    const intervalValue = Number(customConfig?.intervalValue);
    const daysOfWeekList = _.map(customConfig?.daysOfWeek ?? [], (item) => Number(item));
    const canCheckWeekTips =
      customConfig?.customRecurrence &&
      customConfig?.intervalType === 'WEEK' &&
      selectedDate &&
      moment(selectedDate).isValid() &&
      intervalValue > 0 &&
      !_.isEmpty(daysOfWeekList);

    if (!canCheckWeekTips) {
      return '';
    }

    const startMoment = moment(selectedDate);
    const scheduleDateList = getWeekScheduleDateList({
      selectedDate,
      intervalValue,
      daysOfWeek: new Set(daysOfWeekList),
      endDate: customConfig?.endDate,
    });
    // Case A: 检查第一个发送日期, 首次发送日期距离 selectedDate > 7 天
    const firstSendDate = moment(scheduleDateList[0], SCHEDULE_DATE_FORMAT);
    if (firstSendDate.diff(startMoment, 'days', true) > 7) {
      return i18n.t('EHANDOVER_FIRST_SEND_DATE_OVER_7_DAYS', {
        firstSendDate: firstSendDate.format(SCHEDULE_DATE_FORMAT),
      });
    }

    // Case B: 检查选中星期的首次发送日期，某一个星期几的首次发送日期距离 selectedDate > 7 天
    const selectedWeekOptions = _.filter(weekOptions, (item) => daysOfWeekList.includes(Number(item.val)));
    const weekOverLimitInfo = getWeekOverLimitInfo({
      scheduleDateList,
      selectedWeekOptions,
      startMoment,
    });
    if (weekOverLimitInfo) {
      return i18n.t('EHANDOVER_FIRST_CYCLE_SEND_DATE_OVER_7_DAYS', {
        weekDay:
          language === LANGUAGE_EN_US ? WEEK_FULL_NAME_MAP[weekOverLimitInfo.weekVal] : weekOverLimitInfo.weekDay,
        firstSendDate: moment(weekOverLimitInfo.firstSendDate, SCHEDULE_DATE_FORMAT).format(SCHEDULE_DATE_FORMAT),
      });
    }

    return '';
  }, [
    customConfig?.customRecurrence,
    customConfig?.intervalType,
    customConfig?.intervalValue,
    customConfig?.daysOfWeek,
    customConfig?.endDate,
    language,
    selectedDate,
  ]);

  useEffect(() => {
    if (inputRef.current) {
      inputRef.current.focus();
    }
  }, [customConfig?.intervalValue]);

  const CustomRecurrence = () => {
    return (
      <Box display="flex" flexDirection="column" gridGap="1.2rem">
        <Box display="flex" gridGap="1.2rem" alignItems="center" flexWrap="wrap">
          <Box className={classes.repeatLabel}>{i18n.t('EHANDOVER_REPEAT_EVERY')}</Box>
          <InputBase
            inputRef={inputRef} 
            id="ehandover_schedule_send_repeat_num"
            className={classes.inputBase}
            placeholder={i18n.t('EHANDOVER_ENTER_INTERVAL')}
            value={customConfig.intervalValue}
            onChange={(event) => handleChangeRepeatNum(event.target.value)}
            type="text"
            inputProps={{ inputMode: 'numeric', pattern: '[0-9]*' }}
          />
          <AutoCompleteSelect
            id="ehandover_schedule_send_repeat_unit"
            width="12rem"
            height="3.5rem"
            itemList={repeatUnitOptions}
            value={customConfig?.intervalType}
            onChange={(val) => handleChangeCustomConfig('intervalType', val)}
          />
        </Box>
        <Box>
          {customConfig?.intervalType === 'WEEK' && (
            <Box display="flex" flexDirection="row" alignItems="center" gridGap="1.2rem">
              <Box className={classes.repeatLabel}>{i18n.t('EHANDOVER_ON')}</Box>
              <Box
                display="flex"
                gridGap="0.857rem"
                alignItems="center"
                flexWrap="wrap"
                justifyContent="space-evenly"
                width="100%"
              >
                {weekOptions?.map((item, index) => (
                  <Box
                    key={getArrayRandomKey(index, 'ehandover_schedule_send_week')}
                    className={classNames(
                      classes.weekBox,
                      customConfig.daysOfWeek.includes(item.val) && classes.active
                    )}
                    onClick={() => handleSelectDayOfWeek(item.val)}
                  >
                    {item.text}
                  </Box>
                ))}
              </Box>
            </Box>
          )}
          {customConfig?.intervalType === 'MONTH' && (
            <AutoCompleteSelect
              id="ehandover_schedule_send_month"
              width="100%"
              height="3.5rem"
              itemList={monthOptions}
              value={customConfig?.monthRepeatType}
              onChange={(_val, item) => handleChangeMonthRepeatType(item)}
            />
          )}
        </Box>
      </Box>
    );
  };

  return (
    <Dialog className={classes.pop} open={open} onClose={handleDialogClose}>
      <div className={classes.poproot}>
        <div className={classes.head}>
          <div className={classes.title}>{i18n.t('EHANDOVER_CREATENOTE_POST')}</div>
          <IconButton id={`${ehandoverIds.createNote}10`} onClick={handleDialogClose}>
            <CloseIcon className={classes.icon} />
          </IconButton>
        </div>
        <Box display="flex" flexDirection="column" gridGap="2.125rem">
          <Box>
            <div className={classes.context}>{i18n.t('EHANDOVER_CREATENOTE_POST_TITLE')}</div>
            <KeyboardDateTimePicker
              id={`${ehandoverIds.createNote}11`}
              autoOk
              className={classes.datepicker}
              onChange={handleChangePostDate}
              keyboardIcon={<DateIcon className={classes.Picon} />}
              value={selectedDate}
              variant="inline"
              format="yyyy-MM-DD HH:mm"
              inputVariant="outlined"
              InputAdornmentProps={{ position: 'end' }}
              placeholder="Start Date"
              minDate={minDate}
              views={['date', 'hours', "minutes"]}
              disableToolbar={true}
              ampm={false}
              minutesStep={10}
              helperText={disabledEarierThanMinDate? i18n.t('EHANDOVER_SCHEDULE_SEND_MIN_DATE', {mins:minMinutes}): ''}
            />
          </Box>
          <Box>
            <FormControlLabel
              id="bed_transferpop_on_oxygen"
              control={<Checkbox />}
              label={i18n.t('EHANDOVER_CUSTOM_RECURRENCE')}
              className={classes.checkBox}
              checked={customConfig?.customRecurrence}
              disabled={disabledCustomCheckbox || customRecurrenceDisabled}
              onChange={(event) => handleChangeCustomConfig('customRecurrence', event?.target?.checked)}
            />
          </Box>
          {customConfig?.customRecurrence && !customRecurrenceDisabled && (
            <Box display="flex" flexDirection="column" gridGap="1.2rem">
              <CustomRecurrence />
              <Box display="flex" gridGap="0.857rem" alignItems="center">
                <Box className={classes.repeatLabel}>{i18n.t('EHANDOVER_TILL')}</Box>
                <Box flex={1}>
                  <KeyboardDateTimePicker
                    id="ehandover_schedule_send_end_date"
                    autoOk
                    className={classes.datepicker}
                    onChange={handleChangeEndDate}
                    keyboardIcon={<DateIcon className={classes.Picon} />}
                    value={customConfig?.endDate}
                    variant="inline"
                    openTo="year"
                    format={customConfig?.intervalType === 'MONTH' ? 'yyyy-MM' : 'yyyy-MM-DD'}
                    inputVariant="outlined"
                    InputAdornmentProps={{ position: 'end' }}
                    placeholder={i18n.t('EHANDOVER_END_DATE')}
                    minDate={moment(selectedDate).add(1, 'd').format('yyyy-MM-DD 23:59:59')}
                    maxDate={endDateMaxDate}
                    views={customConfig?.intervalType === 'MONTH' ? ['year', 'month'] : ['year', 'month', 'date']}
                    disableToolbar={true}
                    ampm={false}
                  />
                </Box>
                <Box className={classes.repeatLabel}>{i18n.t('EHANDOVER_INCLUSIVELY')}</Box>
              </Box>
              {customConfig?.intervalType === 'WEEK' && weekRepeatTypeTips && (
                <Box className={classes.tips}>{weekRepeatTypeTips}</Box>
              )}
            </Box>
          )}
        </Box>
        <div className={classes.foot}>
          <Button id={`${ehandoverIds.createNote}12`} className={classes.cancel} onClick={handleDialogClose}>
            {i18n.t('EHANDOVER_CREATENOTE_POST_CANCEL')}
          </Button>
          <Button
            id={`${ehandoverIds.createNote}13`}
            className={classes.confirm}
            onClick={handleDialogSubmit}
            style={{
              opacity: shareDisabled ? 0.2 : 1,
              color: shareDisabled && '#FFFFFF',
            }}
            disabled={shareDisabled}
          >
            {i18n.t('EHANDOVER_CREATENOTE_SHARE')}
          </Button>
        </div>
      </div>
    </Dialog>
  );
}
